import React, { useState } from 'react';
import SectionSelector from './SectionSelector';
import FormInput from './components/FormInput';
import FormTextarea from './components/FormTextarea';
import AddButton from './components/AddButton';
import RemoveButton from './components/RemoveButton';
import ArrayFieldCard from './components/ArrayFieldCard';

const ConsultingProfessionalResumeForm = ({ resumeData, onUpdate, validationErrors = {}, onFieldFocus, onFieldBlur, getDynamicSections, renderDynamicSectionForm }) => {
  const [activeSection, setActiveSection] = useState('contact');
  const skillCategories = [
    { key: 'industry_expertise', label: 'Industry Expertise', placeholder: 'Add industry...' },
    { key: 'functional_expertise', label: 'Functional Expertise', placeholder: 'Add functional skill...' },
    { key: 'programming_languages', label: 'Programming Languages', placeholder: 'Add language...' },
    { key: 'databases', label: 'Databases', placeholder: 'Add database...' },
    { key: 'data_analytics_tools', label: 'Data Analytics Tools', placeholder: 'Add analytics tool...' },
    { key: 'data_science_tools', label: 'Data Science Tools', placeholder: 'Add data science tool...' },
    { key: 'big_data_platforms', label: 'Big Data Platforms', placeholder: 'Add platform...' },
  ];

  const [newSkillDrafts, setNewSkillDrafts] = useState(() => {
    const initial = {};
    skillCategories.forEach(({ key }) => {
      initial[key] = '';
    });
    return initial;
  });
  const [isUploading, setIsUploading] = useState(false);

  const updateField = (field, data) => {
    onUpdate((prev) => ({
      ...prev,
      [field]: data,
      isTemplate: false,
    }));
  };

  // Helper function to update key_skills when expertise changes
  const normalizeSkills = (list) =>
    Array.from(
      new Set(
        (list || [])
          .map((skill) => (typeof skill === 'string' ? skill.trim() : skill))
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));

  const aggregateKeySkills = (data) =>
    normalizeSkills([
      ...(Array.isArray(data.industry_expertise) ? data.industry_expertise : []),
      ...(Array.isArray(data.functional_expertise) ? data.functional_expertise : []),
      ...(Array.isArray(data.programming_languages) ? data.programming_languages : []),
      ...(Array.isArray(data.databases) ? data.databases : []),
      ...(Array.isArray(data.data_analytics_tools) ? data.data_analytics_tools : []),
      ...(Array.isArray(data.data_science_tools) ? data.data_science_tools : []),
      ...(Array.isArray(data.big_data_platforms) ? data.big_data_platforms : []),
    ]);

  const updateExpertiseField = (field, data) => {
    onUpdate((prev) => {
      const next = {
        ...prev,
        [field]: normalizeSkills(data),
        isTemplate: false,
      };
      next.key_skills = aggregateKeySkills(next);
      return next;
    });
  };

  const handlePersonalChange = (field, value) => {
    onUpdate((prev) => ({
      ...prev,
      [field]: value,
      isTemplate: false,
    }));
  };

  const handleSkillInputChange = (field, value) => {
    setNewSkillDrafts((prev) => ({ ...prev, [field]: value }));
  };

  const addSkillToCategory = (field) => {
    const draft = (newSkillDrafts[field] || '').trim();
    if (!draft) return;

    setNewSkillDrafts((prev) => ({ ...prev, [field]: '' }));

    onUpdate((prev) => {
      const currentList = Array.isArray(prev[field]) ? prev[field] : [];
      const nextList = normalizeSkills([...currentList, draft]);
      return {
        ...prev,
        [field]: nextList,
        key_skills: aggregateKeySkills({ ...prev, [field]: nextList }),
        isTemplate: false,
      };
    });
  };

  const removeSkillFromCategory = (field, index) => {
    onUpdate((prev) => {
      const list = Array.isArray(prev[field]) ? prev[field] : [];
      const nextList = normalizeSkills(list.filter((_, i) => i !== index));
      return {
        ...prev,
        [field]: nextList,
        key_skills: aggregateKeySkills({ ...prev, [field]: nextList }),
        isTemplate: false,
      };
    });
  };

  const handleClearAllSkills = () => {
    onUpdate((prev) => {
      const cleared = { ...prev, isTemplate: false };
      skillCategories.forEach(({ key }) => {
        cleared[key] = [];
      });
      cleared.key_skills = [];
      return cleared;
    });
    setNewSkillDrafts((prev) => {
      const cleared = { ...prev };
      skillCategories.forEach(({ key }) => {
        cleared[key] = '';
      });
      return cleared;
    });
  };

  const addCert = () => {
    const existing = Array.isArray(resumeData.certifications)
      ? resumeData.certifications
      : [];
    updateField("certifications", [
      ...existing,
      { name: "", provider: "" },
    ]);
  };

  const removeCert = (index) => {
    const existing = Array.isArray(resumeData.certifications)
      ? resumeData.certifications
      : [];
    updateField("certifications", existing.filter((_, i) => i !== index));
  };

  const addEducation = () => {
    updateField("education", [...(resumeData.education || []), {
      degree: "",
      institution: "",
      location: "",
      start_year: "",
      end_year: "",
      description: ""
    }]);
  };

  const updateEducation = (index, field, value) => {
    const updated = [...(resumeData.education || [])];
    updated[index] = { ...updated[index], [field]: value };
    updateField("education", updated);
  };

  const removeEducation = (index) => {
    const list = Array.isArray(resumeData.education)
      ? resumeData.education
      : [];
    updateField("education", list.filter((_, i) => i !== index));
  };

  const determineExperienceKey = (data = resumeData) => {
    if (data.relevant_experience && data.relevant_experience.length > 0) {
      return "relevant_experience";
    }
    return "work_experience";
  };

  const getExperienceArray = () => {
    const key = determineExperienceKey();
    const source = resumeData[key] || [];
    return { key, list: source.map((item) => ({ ...item, description_points: [...(item.description_points || [])] })) };
  };

  const persistExperienceArray = (updatedList) => {
    onUpdate((prev) => {
      const key = determineExperienceKey(prev);
      const next = { ...prev, [key]: updatedList };
      if (key === "relevant_experience") {
        next.work_experience = updatedList;
      } else {
        next.relevant_experience = updatedList;
      }
      return next;
    });
  };

  const addExperience = () => {
    const { list } = getExperienceArray();
    const emptyExp = {
      position: "",
      company: "",
      location: "",
      start_date: "",
      end_date: "",
      description_points: [],
    };
    persistExperienceArray([...list, emptyExp]);
  };

  const updateExperience = (index, field, value) => {
    const { list } = getExperienceArray();
    const updated = list.map((item, idx) =>
      idx === index ? { ...item, [field]: value } : item
    );
    persistExperienceArray(updated);
  };

  const removeExperience = (index) => {
    const { list } = getExperienceArray();
    const updated = list.filter((_, idx) => idx !== index);
    persistExperienceArray(updated);
  };

  const getDescriptionPoints = (exp) => {
    if (!exp) return [];
    if (Array.isArray(exp.description_points)) {
      return exp.description_points;
    }
    if (typeof exp.description_points === "string" && exp.description_points.trim()) {
      return [exp.description_points.trim()];
    }
    return [];
  };

  const addDescriptionPoint = (expIndex) => {
    const { list } = getExperienceArray();
    const updated = list.map((item, idx) => {
      if (idx !== expIndex) return item;
      const points = getDescriptionPoints(item);
      return { ...item, description_points: [...points, ""] };
    });
    persistExperienceArray(updated);
  };

  const updateDescriptionPoint = (expIndex, pointIndex, value) => {
    const { list } = getExperienceArray();
    const updated = list.map((item, idx) => {
      if (idx !== expIndex) return item;
      const points = getDescriptionPoints(item).map((point, pIdx) =>
        pIdx === pointIndex ? value : point
      );
      return { ...item, description_points: points };
    });
    persistExperienceArray(updated);
  };

  const removeDescriptionPoint = (expIndex, pointIndex) => {
    const { list } = getExperienceArray();
    const updated = list.map((item, idx) => {
      if (idx !== expIndex) return item;
      const points = getDescriptionPoints(item).filter((_, pIdx) => pIdx !== pointIndex);
      return { ...item, description_points: points };
    });
    persistExperienceArray(updated);
  };

  const addAchievement = () => {
    updateField("achievements", [...(resumeData.achievements || []), ""]);
  };

  const removeAchievement = (index) => {
    const list = Array.isArray(resumeData.achievements)
      ? resumeData.achievements
      : [];
    updateField("achievements", list.filter((_, i) => i !== index));
  };

  const updateAchievement = (index, value) => {
    const updated = [...(resumeData.achievements || [])];
    updated[index] = value;
    updateField("achievements", updated);
  };

  // Project helper functions
  const addProject = () => {
    updateField("projects", [...(resumeData.projects || []), { title: "", tech_stack: "", description: "" }]);
  };

  const removeProject = (index) => {
    const list = Array.isArray(resumeData.projects) ? resumeData.projects : [];
    updateField("projects", list.filter((_, i) => i !== index));
  };

  const updateProject = (index, field, value) => {
    const updated = [...(resumeData.projects || [])];
    updated[index] = { ...updated[index], [field]: value };
    updateField("projects", updated);
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setIsUploading(true);
      const reader = new FileReader();
      reader.onload = (e) => {
        handlePersonalChange("profile_image_url", e.target.result);
        setIsUploading(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const baseSections = [
    { id: 'contact', label: 'Contact' },
    { id: 'summary', label: 'Summary' },
    { id: 'experience', label: 'Experience' },
    { id: 'skills', label: 'Skills' },
    { id: 'education', label: 'Education' },
    { id: 'certifications', label: 'Certifications' },
    { id: 'projects', label: 'Projects' },
    { id: 'achievements', label: 'Achievements' }
  ];

  const sections = getDynamicSections ? [...baseSections, ...getDynamicSections()] : baseSections;
  const { list: experienceList } = getExperienceArray();
  if (typeof window !== "undefined") {
    console.log("[ConsultingForm] experienceList", experienceList);
  }

  return (
    <div className="resume-form" style={{marginTop: 0, paddingTop: 0}}>
      <div className="form-content" style={{marginTop: 0, paddingTop: 0}}>
        <div className="form-header">
          <h2 className="form-title">Consulting Profile Editor</h2>
        </div>
        <SectionSelector
          sections={sections}
          activeSection={activeSection}
          onSectionChange={setActiveSection}
        />
        <div className="section-form">

      {activeSection === 'contact' && (
        <div className="tab-panel">
          <h3 className="section-title">Contact Information</h3>
          
          <div className={`form-group ${validationErrors.profile_image_url ? 'error' : ''}`}>
            <label>Profile Picture</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              {resumeData?.profile_image_url && (
                <img 
                  src={resumeData.profile_image_url} 
                  alt="Profile Preview" 
                  style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #ddd' }} 
                />
              )}
              <div>
                <input
                  type="file"
                  id="profile-image-upload"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleImageUpload}
                  disabled={isUploading}
                />
                <label htmlFor="profile-image-upload" className="add-button" style={{ cursor: isUploading ? 'not-allowed' : 'pointer', opacity: isUploading ? 0.7 : 1 }}>
                  {isUploading ? 'Uploading...' : 'Upload Photo'}
                </label>
                {resumeData?.profile_image_url && (
                  <button 
                    type="button"
                    onClick={() => handlePersonalChange('profile_image_url', '')} 
                    className="remove-button" 
                    style={{marginLeft: '10px'}}
                  >
                    🗑️ Remove
                  </button>
                )}
              </div>
            </div>
            {validationErrors.profile_image_url && <p className="error-message">{validationErrors.profile_image_url}</p>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormInput
              label="Full Name"
              name="full_name"
              value={resumeData?.full_name || ""}
              onChange={(value) => handlePersonalChange("full_name", value)}
              placeholder="Last Name, First Name"
              maxLength={100}
              error={validationErrors.full_name}
            />
            <FormInput
              label="Job Title"
              name="job_title"
              value={resumeData?.job_title || ""}
              onChange={(value) => handlePersonalChange("job_title", value)}
              placeholder="BP discipline role"
              maxLength={100}
              error={validationErrors.job_title}
            />
            <FormInput
              label="Location"
              name="location"
              value={resumeData?.location || ""}
              onChange={(value) => handlePersonalChange("location", value)}
              placeholder="Country"
              maxLength={100}
              error={validationErrors.location}
            />
            <FormInput
              label="Email"
              name="email"
              type="email"
              value={resumeData?.email || ""}
              onChange={(value) => handlePersonalChange("email", value)}
              placeholder="your.email@example.com"
              maxLength={100}
              error={validationErrors.email}
            />
            <FormInput
              label="Phone"
              name="phone"
              type="tel"
              value={resumeData?.phone || ""}
              onChange={(value) => handlePersonalChange("phone", value)}
              placeholder="+1 (555) 123-4567"
              maxLength={20}
              error={validationErrors.phone}
            />
            <FormInput
              label="LinkedIn"
              name="linkedin"
              type="url"
              value={resumeData?.linkedin || ""}
              onChange={(value) => handlePersonalChange("linkedin", value)}
              placeholder="https://linkedin.com/in/yourprofile"
              maxLength={200}
              error={validationErrors.linkedin}
            />
            <FormInput
              label="GitHub"
              name="github"
              type="url"
              value={resumeData?.github || ""}
              onChange={(value) => handlePersonalChange("github", value)}
              placeholder="https://github.com/yourusername"
              maxLength={200}
              error={validationErrors.github}
            />
          </div>
        </div>
      )}

      {activeSection === 'summary' && (
        <div className="tab-panel">
          <h3 className="section-title">Professional Summary</h3>
          <div className="form-group">
            <textarea
              value={resumeData.professional_summary || ""}
              onChange={(e) => handlePersonalChange("professional_summary", e.target.value)}
              placeholder="Person Name is a B.S. Computer Science graduate with 5+ years of hands-on software engineering experience in energy industry..."
              rows={6}
              maxLength="500"
            />
          </div>
        </div>
      )}

      {activeSection === 'experience' && (
        <div className="tab-panel">
          <div className="section-header">
            <h3 className="section-title">Work Experience</h3>
            <AddButton onClick={addExperience}>Add Experience</AddButton>
          </div>
          {experienceList.length === 0 && (
            <p className="text-sm text-muted-foreground italic">
              No experience entries yet. Click "Add Experience" to create one.
            </p>
          )}
          {experienceList.map((exp, index) => (
            <ArrayFieldCard
              key={index}
              title={`Experience ${index + 1}`}
              index={index}
              onRemove={() => removeExperience(index)}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormInput
                  label="Position"
                  name={`exp-position-${index}`}
                  value={exp.position || ""}
                  onChange={(value) => updateExperience(index, "position", value)}
                  placeholder="Senior Consultant"
                  maxLength={100}
                />
                <FormInput
                  label="Company"
                  name={`exp-company-${index}`}
                  value={exp.company || ""}
                  onChange={(value) => updateExperience(index, "company", value)}
                  placeholder="Company Name"
                  maxLength={100}
                />
                <FormInput
                  label="Location"
                  name={`exp-location-${index}`}
                  value={exp.location || ""}
                  onChange={(value) => updateExperience(index, "location", value)}
                  placeholder="City, Country"
                  maxLength={100}
                />
                <FormInput
                  label="Start Date"
                  name={`exp-start-${index}`}
                  value={exp.start_date || ""}
                  onChange={(value) => updateExperience(index, "start_date", value)}
                  placeholder="Jan 2020"
                  maxLength={20}
                />
                <FormInput
                  label="End Date"
                  name={`exp-end-${index}`}
                  value={exp.end_date || ""}
                  onChange={(value) => updateExperience(index, "end_date", value)}
                  placeholder="Present"
                  maxLength={20}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Description Points</label>
                {getDescriptionPoints(exp).map((point, pIndex) => (
                  <div key={pIndex} className="flex gap-2 items-start">
                    <FormTextarea
                      label=""
                      name={`exp-desc-${index}-${pIndex}`}
                      value={point}
                      onChange={(value) => updateDescriptionPoint(index, pIndex, value)}
                      placeholder="Describe your responsibilities and achievements..."
                      rows={3}
                      maxLength={500}
                      className="flex-1"
                    />
                    <RemoveButton
                      onClick={() => removeDescriptionPoint(index, pIndex)}
                      ariaLabel={`Remove description point ${pIndex + 1}`}
                      size="icon"
                      variant="ghost"
                      className="mt-6"
                    />
                  </div>
                ))}
                <AddButton
                  onClick={() => addDescriptionPoint(index)}
                  variant="outline"
                  size="sm"
                >
                  Add Description Point
                </AddButton>
              </div>
            </ArrayFieldCard>
          ))}
        </div>
      )}

      {activeSection === 'skills' && (
        <div className="tab-panel">
          <div className="section-header">
            <h3 className="section-title">Skills & Expertise</h3>
            <button
              type="button"
              onClick={handleClearAllSkills}
              className="clear-skills-btn"
            >
              🗑️ Clear All Skills
            </button>
          </div>

          {skillCategories.map(({ key, label, placeholder }) => {
            const values = Array.isArray(resumeData[key]) ? resumeData[key] : [];
            return (
              <div key={key} className="space-y-2">
                <label className="text-sm font-medium">{label}</label>
                {values.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">No items added yet.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {values.map((item, index) => (
                      <span key={`${key}-${index}`} className="inline-flex items-center gap-2 rounded-full border px-2.5 py-0.5 text-xs font-semibold bg-secondary text-secondary-foreground">
                        <span>{item}</span>
                        <RemoveButton
                          onClick={() => removeSkillFromCategory(key, index)}
                          ariaLabel={`Remove ${label.toLowerCase()} ${index + 1}`}
                          size="icon"
                          variant="ghost"
                          className="h-4 w-4"
                          useXIcon={true}
                        />
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newSkillDrafts[key] || ''}
                    onChange={(e) => handleSkillInputChange(key, e.target.value)}
                    placeholder={placeholder}
                    maxLength={80}
                    className="flex-1 placeholder:text-muted-foreground/60 placeholder:italic"
                  />
                  <AddButton
                    onClick={() => addSkillToCategory(key)}
                    variant="secondary"
                    size="default"
                  >
                    Add
                  </AddButton>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {activeSection === 'education' && (
        <div className="tab-panel">
          <div className="section-header">
            <h3 className="section-title">Education</h3>
            <AddButton onClick={addEducation}>Add Education</AddButton>
          </div>
          {(resumeData.education || []).map((edu, index) => (
            <ArrayFieldCard
              key={index}
              title={`Education ${index + 1}`}
              index={index}
              onRemove={() => removeEducation(index)}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormInput
                  label="Degree"
                  name={`edu-degree-${index}`}
                  value={edu.degree || ""}
                  onChange={(value) => updateEducation(index, "degree", value)}
                  placeholder="B.S. Computer Science"
                  maxLength={100}
                />
                <FormInput
                  label="Institution"
                  name={`edu-institution-${index}`}
                  value={edu.institution || ""}
                  onChange={(value) => updateEducation(index, "institution", value)}
                  placeholder="University Name"
                  maxLength={100}
                />
                <FormInput
                  label="Location"
                  name={`edu-location-${index}`}
                  value={edu.location || ""}
                  onChange={(value) => updateEducation(index, "location", value)}
                  placeholder="City, Country"
                  maxLength={100}
                />
                <FormInput
                  label="Start Year"
                  name={`edu-start-${index}`}
                  value={edu.start_year || ""}
                  onChange={(value) => updateEducation(index, "start_year", value)}
                  placeholder="2018"
                  maxLength={10}
                />
                <FormInput
                  label="End Year"
                  name={`edu-end-${index}`}
                  value={edu.end_year || ""}
                  onChange={(value) => updateEducation(index, "end_year", value)}
                  placeholder="2022"
                  maxLength={10}
                />
              </div>
            </ArrayFieldCard>
          ))}
        </div>
      )}

      {activeSection === 'achievements' && (
        <div className="tab-panel">
          <div className="section-header">
            <h3 className="section-title">Achievements</h3>
            <AddButton onClick={addAchievement}>Add Achievement</AddButton>
          </div>
          {(resumeData.achievements || []).map((achievement, index) => (
            <div key={index} className="flex gap-2 items-start mb-4">
              <FormInput
                label=""
                name={`achievement-${index}`}
                value={achievement}
                onChange={(value) => updateAchievement(index, value)}
                placeholder="Enter achievement"
                className="flex-1"
              />
              <RemoveButton
                onClick={() => removeAchievement(index)}
                ariaLabel={`Remove achievement ${index + 1}`}
                size="icon"
                variant="ghost"
                className="mt-6"
              />
            </div>
          ))}
        </div>
      )}

      {activeSection === 'projects' && (
        <div className="tab-panel">
          <div className="section-header">
            <h3 className="section-title">Projects</h3>
            <AddButton onClick={addProject}>Add Project</AddButton>
          </div>
          {(resumeData.projects || []).map((project, index) => (
            <ArrayFieldCard
              key={index}
              title={`Project ${index + 1}`}
              index={index}
              onRemove={() => removeProject(index)}
            >
              <FormInput
                label="Project Title"
                name={`proj-title-${index}`}
                value={project.title || project.name || ""}
                onChange={(value) => updateProject(index, "title", value)}
                placeholder="Enter project title"
                maxLength={200}
              />
              <FormInput
                label="Tech Stack"
                name={`proj-tech-${index}`}
                value={
                  Array.isArray(project.tech_stack)
                    ? project.tech_stack.join(', ')
                    : project.tech_stack || ""
                }
                onChange={(value) => updateProject(index, "tech_stack", value)}
                placeholder="e.g., React, Node.js, MongoDB"
                maxLength={200}
              />
              <FormTextarea
                label="Description"
                name={`proj-desc-${index}`}
                value={
                  Array.isArray(project.description)
                    ? project.description.join('\n')
                    : project.description || ""
                }
                onChange={(value) => updateProject(index, "description", value)}
                placeholder="Describe the project..."
                rows={4}
                maxLength={1000}
              />
            </ArrayFieldCard>
          ))}
        </div>
      )}

      {activeSection === 'certifications' && (
        <div className="tab-panel">
          <div className="section-header">
            <h3 className="section-title">Certifications</h3>
            <AddButton onClick={addCert}>Add Certification</AddButton>
          </div>
          {(resumeData.certifications || []).map((cert, index) => (
            <ArrayFieldCard
              key={index}
              title={`Certification ${index + 1}`}
              index={index}
              onRemove={() => removeCert(index)}
            >
              <FormInput
                label="Certification Name"
                name={`cert-name-${index}`}
                value={typeof cert === 'string' ? cert : cert.name || ""}
                onChange={(value) => {
                  const updated = [...(resumeData.certifications || [])];
                  if (typeof cert === 'string') {
                    updated[index] = value;
                  } else {
                    updated[index] = { ...cert, name: value };
                  }
                  updateField("certifications", updated);
                }}
                placeholder="Certification Name"
                maxLength={100}
              />
              <FormInput
                label="Provider"
                name={`cert-provider-${index}`}
                value={typeof cert === 'string' ? "" : cert.provider || ""}
                onChange={(value) => {
                  const updated = [...(resumeData.certifications || [])];
                  if (typeof cert === 'string') {
                    updated[index] = { name: cert, provider: value };
                  } else {
                    updated[index] = { ...cert, provider: value };
                  }
                  updateField("certifications", updated);
                }}
                placeholder="Certification Provider"
                maxLength={100}
              />
            </ArrayFieldCard>
          ))}
        </div>
      )}

          <div className="dynamic-sections-container">
            {renderDynamicSectionForm &&
              getDynamicSections &&
              getDynamicSections().map((section) => {
                if (
                  activeSection === section.id &&
                  !baseSections.find((s) => s.id === section.id)
                ) {
                  return (
                    <div key={section.id} className="tab-panel">
                      {renderDynamicSectionForm(section.id)}
                    </div>
                  );
                }
                return null;
              })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultingProfessionalResumeForm;
